import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Oval as Loader } from "react-loader-spinner";
import "./report-modal.css";
import { showError } from "../../../shared/utils/swalHandler.js";
import { REPORT_REASONS } from "../../../shared/constants/data.js";
import { reportContentCreator } from "../../../shared/actions/helpAction.js";
import { useModalOpen } from "../../../shared/utils/modalUtils.js";

/**
 * ReportWhyModal Component
 * 
 * Modal for reporting a profile for displaying inappropriate content
 * 
 * @param {boolean} modalOpen - Controls visibility of the modal
 * @param {Function} onClose - Callback function called when modal is closed
 * @param {string} creatorId - ID of the creator being reported
 */
const ReportWhyModal = ({ 
  modalOpen, 
  onClose, 
  creatorId,
  isSinglePost = false,
  postId = null,
}) => {
  const [modal, setModal] = useState(modalOpen);
  const [reportLoading, setReportLoading] = useState(false);
  const [submittingReason, setSubmittingReason] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isBrowser, setIsBrowser] = useState(false);

  // Set browser flag for SSR safety
  useEffect(() => {
    setIsBrowser(true);
  }, []);

  // Update modal state when prop changes
  useEffect(() => {
    setModal(modalOpen);
  }, [modalOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!modal) {
      setReportLoading(false);
      setSubmittingReason(null);
      setIsSubmitted(false);
    }
  }, [modal]);

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(modal);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (reportLoading) return; // Prevent closing during submission
    setModal(false);
    onClose?.();
  }, [onClose, reportLoading]);

  // Handle report submission
  const handleReport = useCallback(async (reason) => {
    // Prevent multiple submissions
    if (reportLoading || isSubmitted) return;

    const reportData = {
      reason: reason,
      ...(isSinglePost && postId && { content_id: postId }),
    };
    setReportLoading(true);
    setSubmittingReason(reason);

    try {
      const result = await reportContentCreator(creatorId, reportData);

      // Check both HTTP status and data status (if present) to match original behavior
      const isSuccess = result.success &&
        (result.status === 200 || result.data?.status === 200);

      if (isSuccess) {
        setIsSubmitted(true);
      } else {
        const errorMessage = result.error || 
          "Unable to submit report. Please try again later.";

        if (result.status === 422) {
          await showError("Sorry!", errorMessage);
        } else {
          await showError("Error", errorMessage);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
        error.message || 
        "Something went wrong. Please try again later.";
      await showError("Error!", errorMessage);
    } finally {
      setReportLoading(false);
      setSubmittingReason(null);
    }
  }, [creatorId, reportLoading, isSubmitted]);

  if (!modal || !isBrowser) {
    return null;
  }

  const modalMarkup = (
    <div className="report-modal-overlay cm_bg_blur">
      <div
        className="report-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="report-modal-header">
          <h3 className="report-modal-title">{isSinglePost ? "Report Post" : "Report Profile"}</h3>
          <button
            type="button"
            className="report-modal-close"
            onClick={handleClose}
            disabled={reportLoading}
            aria-label="Close"
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="report-modal-body">
          {isSubmitted ? (
            <div className="report-modal-success">
              <p className="report-modal-success-message">
                Thank you for helping to keep our community safe. We have received
                your report and will review it shortly.
              </p>
            </div>
          ) : (
            <>
              <div className="report-modal-description">
                <p>Why are you reporting this {isSinglePost ? "post" : "account"}?</p>
              </div>

              <div className="report-modal-user-list">
                {REPORT_REASONS.map((reason, index) => {
                  const isSubmitting = reportLoading && submittingReason === reason;
                  
                  return (
                    <div
                      key={`${reason}-${index}`}
                      className="report-modal-user-item"
                    >
                      <div className="report-modal-user-info">
                        <div className="report-modal-user-username">{reason}</div>
                      </div>
                      <button
                        type="button"
                        className="report-modal-user-button"
                        onClick={() => handleReport(reason)}
                        disabled={reportLoading}
                        aria-label={`Report for ${reason}`}
                        aria-busy={isSubmitting}
                      >
                        {isSubmitting ? (
                          <div className="report-modal-user-button-loading">
                            <Loader
                              type="Oval"
                              color="#ffffff"
                              height={16}
                              width={16}
                              ariaLabel="Submitting report"
                            />
                            <span>Submitting...</span>
                          </div>
                        ) : (
                          "Report"
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalMarkup, document.body);
};

export default ReportWhyModal;
